import styles from './edit-participants-dialog.module.scss';
import { Col, Row } from 'reactstrap';
import { useImmer } from 'use-immer';
import { ChangeEvent, useContext, useEffect, useState } from 'react';
import { makeCourseRequest, useCourseGetRequestHook } from '../../../requestUtils';
import { CheckboxLabel, Dialog, DialogActions, DialogContent, DialogTitle, GrayOutlinedButton, LoadingButton, MultiLineTextField, SnackBarContext, TextField } from '@frontend-workspace/react-shared-lib';
import { IRegistration } from '../../../_models/registration';
import { useTranslation } from 'react-i18next';

/* eslint-disable-next-line */
export interface EditRegistrationDialogProps {
  onClose: () => void,
  refreshData: () => void,
  open: boolean,
  courseId: string
}

export function EditRegistrationDialog(props: EditRegistrationDialogProps) {
  const { data }: { data: IRegistration } = useCourseGetRequestHook(`course/registration/${props.courseId}`, undefined);
  const [registration, updateRegistration] = useImmer(data);

  const { t } = useTranslation();
  const { setSnackbarMessage } = useContext(SnackBarContext);
  const [loading, setLoading] = useState<boolean>(false);

  const handleSendEmailChange = (event: any) => updateRegistration(draft => { draft.sendEmail = event.target.checked });
  const handleEmailChange = (event: ChangeEvent<HTMLInputElement>) => updateRegistration(draft => { draft.person.email = event.target.value });
  const handleFirstNameChange = (event: ChangeEvent<HTMLInputElement>) => updateRegistration(draft => { draft.person.firstName = event.target.value });
  const handleLastNameChange = (event: ChangeEvent<HTMLInputElement>) => updateRegistration(draft => { draft.person.lastName = event.target.value });
  const handleMobileChange = (event: ChangeEvent<HTMLInputElement>) => updateRegistration(draft => { draft.person.mobile = event.target.value });
  const handleCompanyChange = (event: ChangeEvent<HTMLInputElement>) => updateRegistration(draft => { draft.person.companyName = event.target.value });

  const handleAddressChange = (event: ChangeEvent<HTMLInputElement>) => updateRegistration(draft => { draft.company.address = event.target.value });
  const handlePostCodeChange = (event: ChangeEvent<HTMLInputElement>) => updateRegistration(draft => { draft.company.postCode = event.target.value });
  const handleCityChange = (event: ChangeEvent<HTMLInputElement>) => updateRegistration(draft => { draft.company.city = event.target.value });

  const handleInvoiceContactPersonChange = (event: ChangeEvent<HTMLInputElement>) => updateRegistration(draft => { draft.invoiceInfo.contactPersonName = event.target.value });
  const handleInvoiceEmailChange = (event: ChangeEvent<HTMLInputElement>) => updateRegistration(draft => { draft.invoiceInfo.invoiceEmail = event.target.value });
  const handleOrderReferenceChange = (event: ChangeEvent<HTMLInputElement>) => updateRegistration(draft => { draft.invoiceInfo.orderReference = event.target.value });
  const handleEmployeeNumberChange = (event: ChangeEvent<HTMLInputElement>) => updateRegistration(draft => { draft.invoiceInfo.employeeNumber = event.target.value });

  const handleCommentsChange = (event: ChangeEvent<HTMLInputElement>) => updateRegistration(draft => { draft.comments = event.target.value });

  useEffect(() => {
    if (data) {
      updateRegistration(data);
    }
  }, [data]);

  const missingFields = (): boolean => {
    const requiredFieldsFilled =
      registration?.person?.email &&
      registration?.person?.firstName &&
      registration?.person?.lastName &&
      registration?.person?.mobile &&
      registration?.person?.companyName &&
      registration?.company?.address &&
      registration?.company?.postCode &&
      registration?.company?.city
    return requiredFieldsFilled ? false : true;
  };

  const handleUpdateRegistration = () => {
    setLoading(true);
    makeCourseRequest(`course/registration/update`, 'POST', registration).then(result => {
      setSnackbarMessage({ content: t('editRegistration.changedCourseParticipantInList'), type: "info", });
      setLoading(false);
      props.onClose();
      props.refreshData();
    }).catch(() => setSnackbarMessage({ content: t('editRegistration.couldNotChangeCourseParticipantInList'), type: "error" }));
  };

  return (
    <Dialog
      open={props.open}
      maxWidth="lg"
      fullWidth>
      <DialogTitle className='mb-4'>
        {t('editRegistration.changeCourseParticipant')}
      </DialogTitle>
      <DialogContent>
        <div className='d-flex mb-3'>
          <CheckboxLabel
            label={t('editRegistration.sendEmailOnUpdating')}
            checked={registration?.sendEmail || false}
            onChange={handleSendEmailChange}
          />
        </div>
        <Row>
          <Col xl={6}>
            <div className='mb-3'>
              <TextField
                label={<span>{t('standard.email')} &#42;</span>}
                name='email'
                type='email'
                fullWidth
                sx={{ minWidth: 300 }}
                value={registration?.person?.email || ''}
                onChange={handleEmailChange}
              />
            </div>
            <div className='mb-3'>
              <TextField
                label={<span>{t('standard.firstName')} &#42;</span>}
                name='firstName'
                fullWidth
                sx={{ minWidth: 300 }}
                value={registration?.person?.firstName || ''}
                onChange={handleFirstNameChange}
              />
            </div>
            <div className='mb-3'>
              <TextField
                label={<span>{t('standard.lastName')} &#42;</span>}
                name='lastName'
                fullWidth
                sx={{ minWidth: 300 }}
                value={registration?.person?.lastName || ''}
                onChange={handleLastNameChange}
              />
            </div>
            <Row>
              <Col xl={5}>
                <div className='mb-3'>
                  <TextField
                    label={<span>{t('standard.mobileNo')} &#42;</span>}
                    name='mobile'
                    type='tel'
                    fullWidth
                    value={registration?.person?.mobile || ''}
                    onChange={handleMobileChange}
                  />
                </div>
              </Col>
            </Row>
            <div className='mb-3'>
              <TextField
                label={<span>{t('standard.company')} &#42;</span>}
                name='company'
                fullWidth
                sx={{ minWidth: 300 }}
                value={registration?.person?.companyName || ''}
                onChange={handleCompanyChange}
              />
            </div>
            <Row>
              <Col xl={12}>
                <div className='mb-3'>
                  <TextField
                    label={<span>{t('standard.address')} &#42;</span>}
                    name='address'
                    fullWidth
                    sx={{ minWidth: 300 }}
                    value={registration?.company?.address || ''}
                    onChange={handleAddressChange}
                  />
                </div>
                <Row>
                  <Col xl={4}>
                    <div className='mb-3'>
                      <TextField
                        label={<span>{t('standard.postCode')} &#42;</span>}
                        name='postCode'
                        fullWidth
                        value={registration?.company?.postCode || ''}
                        onChange={handlePostCodeChange}
                      />
                    </div>
                  </Col>
                  <Col xl={8}>
                    <div className='mb-3'>
                      <TextField
                        label={<span>{t('standard.place')} &#42;</span>}
                        name='city'
                        fullWidth
                        value={registration?.company?.city || ''}
                        onChange={handleCityChange}
                      />
                    </div>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Col>
          <Col xl={6}>
            <div className='mb-3'>
              <TextField
                label={t('standard.contactPersonForInvoice')}
                name='invoiceContactPerson'
                fullWidth
                sx={{ minWidth: 300 }}
                value={registration?.invoiceInfo?.contactPersonName || ''}
                onChange={handleInvoiceContactPersonChange}
              />
            </div>
            <div className='mb-3'>
              <TextField
                label={t('standard.invoiceEmail')}
                name='invoiceEmail'
                fullWidth
                sx={{ minWidth: 300 }}
                value={registration?.invoiceInfo?.invoiceEmail || ''}
                onChange={handleInvoiceEmailChange}
              />
            </div>
            <div className='mb-3'>
              <TextField
                label={t('standard.orderReference')}
                name='orderReference'
                fullWidth
                sx={{ minWidth: 300 }}
                value={registration?.invoiceInfo?.orderReference || ''}
                onChange={handleOrderReferenceChange}
              />
            </div>
            <Row>
              <Col xl={5}>
                <div className='mb-3'>
                  <TextField
                    label={t('standard.employeeNumber')}
                    name='employeeNumber'
                    fullWidth
                    value={registration?.invoiceInfo?.employeeNumber || ''}
                    onChange={handleEmployeeNumberChange}
                  />
                </div>
              </Col>
            </Row>
            {/* Todo:: Geir ønsker org.nr */}
            <div className='mb-3'>
              <TextField
                label={t('standard.organisationNumber')}
                name='Org.no.'
                fullWidth
                sx={{ minWidth: 300 }}
                disabled
              // value={}
              // onChange={}
              />
            </div>
          </Col>
        </Row>
        <Row>
          <Col>
            <div className='mb-3'>
              <MultiLineTextField
                label={t('standard.otherComments')}
                name='comments'
                fullWidth
                minRows={1}
                value={registration?.comments || ''}
                onChange={handleCommentsChange}
              />
            </div>
          </Col>
        </Row>
      </DialogContent>
      <DialogActions>
        <LoadingButton
          variant="contained"
          size='large'
          onClick={handleUpdateRegistration}
          disabled={loading || missingFields()}
          loading={loading}
          className='minButtonWidth'>
          {loading ? '' : t('editRegistration.updateCourseParticipant')}
        </LoadingButton>
        <GrayOutlinedButton
          variant="outlined"
          size='large'
          onClick={props.onClose}
          className='minButtonWidth'>
          {t('standard.cancel')}
        </GrayOutlinedButton>
      </DialogActions>
    </Dialog>
  );
}

export default EditRegistrationDialog;
